import { useMemo, useState } from "react";
import { MatchEvent } from "../../../../store/useMatchLogStore";
import { Match } from "../../types";
import { usePlayerStats } from "../../hooks/usePlayerStats";
import { MatchAnalytics } from "../molecules/MatchAnalytics";
import { PlayerStatsTable } from "../molecules/PlayerStatsTable";
import HeatMapSection from "../molecules/HeatMapSection";
import LiveMatchContextBanner from "../molecules/LiveMatchContextBanner";
import PlayerAnalyticsView from "../molecules/PlayerAnalyticsView";

interface AnalyticsViewProps {
  match: Match;
  liveEvents: MatchEvent[];
  queuedEvents: MatchEvent[];
  onFieldIds: { home: Set<string>; away: Set<string> };
  effectiveClock: string;
  globalClock: string;
  effectiveTime: number;
  varTimeSeconds: number;
  timeoutTimeSeconds: number;
  ineffectiveBreakdown: any;
  t: any;
}

export default function AnalyticsView({
  match,
  liveEvents,
  queuedEvents,
  onFieldIds,
  effectiveClock,
  globalClock,
  effectiveTime,
  varTimeSeconds,
  timeoutTimeSeconds,
  ineffectiveBreakdown,
  t,
}: AnalyticsViewProps) {
  const [activeTab, setActiveTab] = useState<"general" | "player">("general");
  const allEvents = useMemo(
    () => [...liveEvents, ...queuedEvents],
    [liveEvents, queuedEvents],
  );
  const playerStats = usePlayerStats(match, allEvents);
  const onCourtHomePlayers = useMemo(
    () =>
      (match.home_team.players ?? []).filter((player) =>
        onFieldIds.home.has(player.id),
      ),
    [match, onFieldIds.home],
  );
  const onCourtAwayPlayers = useMemo(
    () =>
      (match.away_team.players ?? []).filter((player) =>
        onFieldIds.away.has(player.id),
      ),
    [match, onFieldIds.away],
  );

  return (
    <div className="mb-6 flex flex-col gap-3">
      <LiveMatchContextBanner
        match={match}
        onCourtHomePlayers={onCourtHomePlayers}
        onCourtAwayPlayers={onCourtAwayPlayers}
        effectiveTime={effectiveTime}
        ineffectiveSeconds={match?.ineffective_time_seconds || 0}
        t={t}
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="analytics-tab-general"
          onClick={() => setActiveTab("general")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === "general"
              ? "bg-orange-600 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          {t("analytics.tabGeneral", "General")}
        </button>
        <button
          type="button"
          data-testid="analytics-tab-player"
          onClick={() => setActiveTab("player")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === "player"
              ? "bg-orange-600 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          {t("analytics.tabPlayer", "Per Player")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-900/60 border border-slate-800 px-3 py-2">
        <div className="flex items-center gap-2 text-slate-200 text-sm">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {t("effectiveTime", "Effective Time")}
          </span>
          <span
            className="font-mono font-semibold"
            data-testid="effective-clock-value"
          >
            {effectiveClock}
          </span>
        </div>
        <div className="flex items-center gap-2 text-slate-200 text-sm">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {t("globalClock", "Global Clock")}
          </span>
          <span className="font-mono font-semibold">{globalClock}</span>
        </div>
      </div>

      {activeTab === "general" ? (
        <>
          <MatchAnalytics
            match={match}
            events={allEvents}
            effectiveTime={effectiveTime}
            varTimeSeconds={varTimeSeconds}
            timeoutSeconds={timeoutTimeSeconds}
            ineffectiveSeconds={match?.ineffective_time_seconds || 0}
            ineffectiveBreakdown={ineffectiveBreakdown}
            t={t}
          />
          <HeatMapSection match={match} events={allEvents} t={t} />
          <PlayerStatsTable
            stats={playerStats}
            homeTeamName={match.home_team.short_name}
            awayTeamName={match.away_team.short_name}
            t={t}
          />
        </>
      ) : (
        <PlayerAnalyticsView
          match={match}
          events={allEvents}
          playerStats={playerStats}
          t={t}
        />
      )}
    </div>
  );
}
